import React, {
    forwardRef,
    useCallback,
    useState,
    type RefObject,
} from 'react';
import styled from 'styled-components';

import { variables } from '@splunk/themes';
import type { Mode } from '@splunk/dashboard-types';

import { CollapseHandle } from './CollapseHandle';
import { Description } from './Description';
import { TitleContainer } from './TitleContainer';

const HeaderContainer = styled.div`
    padding-block: ${variables.spacingSmall} ${variables.spacingXSmall};
    padding-inline: ${variables.spacingLarge} ${variables.spacingSmall};
    background-color: ${variables.backgroundColorPage};
    box-sizing: border-box;
`;

export interface CommonHeaderProps {
    descRef?: RefObject<HTMLElement>;
    descriptionValue?: string;
    handleRef?: RefObject<HTMLButtonElement>;
    isExpanded?: boolean;
    mode: Mode;
    onDescriptionChange: (value?: string) => void;
    onExpandToggle?: () => void;
    onTitleChange: (value?: string) => void;
    showCollapseHandle?: boolean;
    showHeader?: boolean;
    titleRef?: RefObject<HTMLElement>;
    titleValue?: string;
}

export const CommonHeader = forwardRef<HTMLDivElement, CommonHeaderProps>(
    (
        {
            descRef,
            descriptionValue,
            handleRef,
            isExpanded: controlledIsExpanded,
            mode,
            onDescriptionChange,
            onExpandToggle,
            onTitleChange,
            showCollapseHandle = true,
            showHeader = true,
            titleRef,
            titleValue,
        },
        ref
    ) => {
        const [uncontrolledIsExpanded, setUncontrolledIsExpanded] =
            useState(true);

        const isExpanded = controlledIsExpanded ?? uncontrolledIsExpanded;

        const handleExpandToggle = useCallback(() => {
            if (onExpandToggle) {
                onExpandToggle();
                return;
            }
            setUncontrolledIsExpanded((current) => !current);
        }, [onExpandToggle]);

        if (
            !showHeader ||
            (!titleValue && !descriptionValue && mode === 'view')
        ) {
            return null;
        }

        return (
            <HeaderContainer data-test="dashboard-header" ref={ref}>
                <TitleContainer
                    titleRef={titleRef}
                    isExpanded={isExpanded}
                    mode={mode}
                    onValueChange={onTitleChange}
                    value={titleValue}
                >
                    {showCollapseHandle ? (
                        <CollapseHandle
                            ref={handleRef}
                            isExpanded={isExpanded}
                            mode={mode}
                            onClick={handleExpandToggle}
                            valueDesc={descriptionValue}
                        />
                    ) : null}
                </TitleContainer>
                <Description
                    ref={descRef}
                    showInViewMode={isExpanded}
                    mode={mode}
                    onValueChange={onDescriptionChange}
                    value={descriptionValue}
                />
            </HeaderContainer>
        );
    }
);
